// Karttanäkymä: tutkiminen, retken aloitus ja tallennus (suunnitelman kohdat 4 ja 12).

import { gpsStatus, saveStatus } from "../lib/recStatus.js";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

import { duration, km } from "../lib/format.js";
import { discardSummary } from "../lib/discardText.js";
import { distanceM, tileKey } from "../lib/geo.js";
import { MODES, isEligible, modeLabel } from "../lib/modes.js";
import { BASEMAPS } from "../lib/basemap.js";
import { familyLayerData } from "../lib/familyLayer.js";
import { shapeLegend } from "../lib/shapeLayers.js";
import { HEAT_STEPS } from "../lib/heatColors.js";
import { ringsOf } from "../lib/hidden.js";
import { startView } from "../lib/mapStart.js";
import { editingParts } from "../lib/routeGeometry.js";
import { modesText } from "../lib/shapes.js";
import { IconAddTile, IconAdventure, IconChevron, IconLayers, IconLegend, IconLocate, IconStart } from "./icons.jsx";
import { HiddenPickPanel, ImpossiblePanel, PoiPlacePanel, RouteDrawPanel, TilePickPanel, TimePanel } from "./MapTools.jsx";
import { PoiBar, RouteBar } from "./RouteBar.jsx";
import { AdventureBar, AdventureDoneSheet, AdventureSheet } from "./AdventureViews.jsx";
import { MapView } from "./MapView.jsx";
import { OfflineMapPanel } from "./OfflineMapPanel.jsx";
import { ModePicker } from "./ModePicker.jsx";
import { CompanionPicker, PlayerBadge } from "./PlayerViews.jsx";
import { Sheet } from "./Sheet.jsx";

// Lyhyet nimet kulkutapasuodattimen painikkeisiin.
const SHORT_MODE = { ebike: "Sähköpyörä", paddle: "Melonta", snowshoe: "Lumikenkä", kickscooter: "Potkulauta", wheelchair: "Pyörätuoli" };
const FILTER_MODES = MODES.filter((m) => m.eligible);
const OVERLAY_OPTIONS = [
  { id: "pois", label: "Kohteet" },
  { id: "routes", label: "Reitit" },
  { id: "shapes", label: "Maksimineliö ja -klusteri" },
  { id: "next", label: "Seuraavan neliön puuttuvat ruudut" },
  { id: "shapesLarge", label: "Suurten ruutujen neliö ja klusteri" },
  { id: "heat", label: "Lämpökartta" },
  { id: "impossible", label: "Mahdottomat ruudut" },
  { id: "family", label: "Perheen yhteiskartta", needsFamily: true },
];

function useNow(active) {
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export function MapScreen({ app }) {
  const { stats, tiles, content, rec, me, theme, skin, simulator, tool } = app;
  const active = rec.active;
  const [picker, setPicker] = useState(null); // 'start' | 'change' | null
  const [confirmStop, setConfirmStop] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false); // toinen varmistus: retken hylkäys
  const [legend, setLegend] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [adventureOpen, setAdventureOpen] = useState(false);
  const [follow, setFollow] = useState(true);
  const mapRef = useRef(null);
  const simClock = useRef(0);
  const [heat, setHeat] = useState(null);
  const [familyData, setFamilyData] = useState(null); // /api/family/tiles
  const now = useNow(Boolean(active));

  // Kehitystila (?dev=1): kartta selaintestien ulottuville, jotta testi voi siirtää näkymän rastille.
  useEffect(() => {
    if (simulator) window.__tilehuntMap = mapRef.current;
  }, []);

  // Retken alkaessa kartta tarkennetaan ruututasolle, jotta uudet ruudut
  // näkyvät heti kunnolla. Käyttäjän oma tarkempi zoom säilyy.
  const activeTripId = active?.id;
  useEffect(() => {
    const m = mapRef.current;
    if (!activeTripId || !m) return;
    setFollow(true);
    const zoom = Math.max(m.getZoom(), 16);
    if (me) m.setView([me.latitude, me.longitude], zoom);
    else m.setZoom(zoom);
  }, [activeTripId]);

  // Kartta aukeaa pelaajan kohdalle. Sijainti tunnetaan sovelluksen käynnistyessä tehdystä kertahausta
  // (main.jsx), joka voi valmistua vasta kartan avauduttua, joten keskitys tehdään sekä kartan
  // avautuessa (me jo tiedossa) että aina uuden keskityspyynnön tullessa (käynnistys, paluu taustalta).
  // Ei keskitetä, jos retki on käynnissä (seuranta hoitaa sen), toisesta näkymästä on pyydetty tarkennus
  // tai työkalu on auki, eikä myöskään jos käyttäjä ehti jo itse siirtää karttaa (paitsi `force`).
  const touched = useRef(false);
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return undefined;
    const mark = () => (touched.current = true);
    m.on("dragstart", mark);
    return () => m.off("dragstart", mark);
  }, []);
  const centerId = app.centerRequest?.id;
  useEffect(() => {
    const m = mapRef.current;
    const view = m && startView(me, m.getZoom());
    if (!view || active || app.focus || tool) return;
    if (touched.current && !app.centerRequest?.force) return;
    m.setView(view.center, view.zoom);
    touched.current = false;
  }, [centerId, Boolean(me)]);

  // Toisesta näkymästä (esim. Passin neliökortti) pyydetty tarkennus.
  useEffect(() => {
    const m = mapRef.current;
    if (!app.focus || !m) return;
    setFollow(false);
    // Alareunaan jätetään tilaa, jos kohteen päälle tulee paneeli (esim. seikkailupalkki).
    m.fitBounds(app.focus.bounds, { paddingTopLeft: [40, 40], paddingBottomRight: [40, 40 + (app.focus.padBottom ?? 0)], maxZoom: 17 });
    app.clearFocus();
  }, [app.focus]);

  // Työkalun alkaessa kartta siirretään oikeaan kohtaan.
  const toolId = tool && `${tool.type}:${tool.id ?? ""}:${tool.center ?? ""}:${tool.partIndex ?? ""}`;
  useEffect(() => {
    const m = mapRef.current;
    if (!tool || !m || tool.type === "time") return; // aikakone ei siirrä karttaa
    setFollow(false);
    if (tool.center) m.setView(tool.center, 16);
    else if ((tool.type === "poi" || tool.type === "hiddenpick") && tool.start) m.setView(tool.start, 17);
    else if (tool.type === "route" && tool.coords.length > 1) m.fitBounds(tool.coords, { padding: [40, 40] });
    else if (tool.type === "impossible") {
      const minZoom = tool.level === "bigtile" ? 11 : 15;
      if (m.getZoom() < minZoom) m.setZoom(minZoom);
    } else if (m.getZoom() < 15) m.setZoom(15);
  }, [toolId]);

  // Lämpökartta haetaan näkyvälle alueelle aina kartan liikuttua. Solun koko on
  // noin 16 kuvapistettä: tarkka tie erottuu, mutta soluja on vain muutama tuhat.
  const heatOn = app.overlays.heat;
  const heatUntil = tool?.type === "time" && tool.until ? Math.round(tool.until) : undefined;
  const modesKey = app.mapModes.join(",");
  useEffect(() => {
    const m = mapRef.current;
    if (!heatOn || !m) {
      setHeat(null);
      return undefined;
    }
    let timer;
    let alive = true;
    const load = async () => {
      const b = m.getBounds().pad(0.25);
      const zoom = Math.max(10, Math.min(19, Math.round(m.getZoom()) + 4));
      try {
        const data = await app.api.heatmap({ south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() }, zoom, app.mapModes, heatUntil);
        if (alive) setHeat(data);
      } catch {
        /* kartta jää ilman lämpökarttaa; seuraava liike yrittää uudelleen */
      }
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(load, 250);
    };
    m.on("moveend", schedule);
    schedule();
    return () => {
      alive = false;
      clearTimeout(timer);
      m.off("moveend", schedule);
    };
  }, [heatOn, modesKey, app.dataVersion, heatUntil]);

  // Perheen yhteiskartta haetaan, kun kerros on päällä, ja uudelleen aina kun data muuttuu
  // (esim. retki päättyi). Epäonnistunut haku jättää edellisen kuvan näkyviin.
  const familyOn = app.overlays.family && (app.players?.length ?? 0) > 1;
  useEffect(() => {
    if (!familyOn) {
      setFamilyData(null);
      return undefined;
    }
    let alive = true;
    app.api.familyTiles().then((data) => alive && setFamilyData(data), () => {});
    return () => { alive = false; };
  }, [familyOn, app.dataVersion]);
  const family = useMemo(() => (familyOn && familyData ? familyLayerData(familyData) : null), [familyOn, familyData]);

  const draft = useMemo(() => {
    if (!tool) return null;
    if (tool.type === "poi" || tool.type === "hiddenpick") return { kind: "circle", radius: tool.radius };
    if (tool.type === "route") return { kind: "line", coords: tool.coords,
      otherParts: editingParts(tool).filter((_, i) => i !== (tool.partIndex ?? 0)) };
    if (tool.type === "time") return null;
    if (tool.type === "impossible") return { kind: "tile", key: tool.key, zoom: tool.level === "bigtile" ? tiles?.large_zoom ?? 14 : tiles?.small_zoom ?? 17 };
    return { kind: "tile", key: tool.key };
  }, [tool, tiles?.small_zoom, tiles?.large_zoom]);

  const toolClick = tool
    ? (lat, lon) => {
        // Funktiomuotoiset päivitykset: nopeat peräkkäiset napautukset eivät
        // ylikirjoita toisiaan vanhentuneella tilalla.
        if (tool.type === "route") app.setTool((t) => ({ ...t, coords: [...t.coords, [lat, lon]] }));
        if (tool.type === "tile") app.setTool((t) => ({ ...t, key: tileKey(lat, lon, tiles?.small_zoom ?? 17) }));
        if (tool.type === "impossible") {
          app.setTool((t) => ({ ...t, key: tileKey(lat, lon, t.level === "bigtile" ? tiles?.large_zoom ?? 14 : tiles?.small_zoom ?? 17) }));
        }
      }
    : null;

  // Kesto ilman taukoja; käynnissä oleva tauko pysäyttää laskurin.
  const elapsed = active ? now - active.startedAt - (active.pausedTotal ?? 0) - (active.pausedAt ? now - active.pausedAt : 0) : 0;

  const pendingTiles = active ? active.localTiles.filter((k) => !tiles?.small.some((t) => t.key === k)).length : 0;

  // Simulaattori: napautus kävelee pisteeseen ~1,4 m/s nopeudella 5 m välein.
  const simWalk = simulator
    ? async (lat, lon) => {
        if (!active) return app.toast({ kind: "info", what: "Aloita retki ensin", sub: "Simulaattori lähettää sijainteja vain retken aikana." });
        const from = me ?? { latitude: lat, longitude: lon };
        const total = distanceM(from.latitude, from.longitude, lat, lon);
        const steps = Math.max(1, Math.round(total / 5));
        // Simuloitu kello etenee kävelyvauhtia eikä koskaan taaksepäin,
        // vaikka napautuksia tulee nopeammin kuin "kävely" kestäisi.
        let t = Math.max(Date.now(), simClock.current);
        for (let i = 1; i <= steps; i++) {
          const f = i / steps;
          t += (total / steps / 1.4) * 1000;
          simulator.emit(from.latitude + (lat - from.latitude) * f, from.longitude + (lon - from.longitude) * f, t);
        }
        simClock.current = t;
      }
    : null;

  return (
    <div class="view">
      <MapView
        basemap={app.basemap}
        tiles={tool?.type === "time" && tool.tiles ? tool.tiles : app.filteredTiles ?? tiles}
        collected={tiles}
        shapes={tool?.type === "time" ? null : app.shapes}
        overlays={app.overlays}
        heat={heat}
        family={family}
        adventure={app.adventure}
        content={content}
        hintRings={ringsOf(app.hints)}
        active={active}
        me={me}
        follow={follow && Boolean(active)}
        theme={`${theme}:${skin}`}
        mapRef={mapRef}
        draft={draft}
        onMapClick={toolClick ?? simWalk}
        selectedRoute={app.mapRoute}
        selectedPoi={app.mapPoi}
        onSelectPoi={(id) => app.openContent("poi", id)}
        onSelectRoute={(id) => app.openContent("route", id)}
        onSelectHidden={(id) => app.openContent("hidden", id)}
      />

      <div class="map-top">
        <div class="map-top-left">
          <div class="score" aria-label="Edistyminen">
            <b class="num">{stats?.small_tiles ?? 0}</b>
            <b class="num">{stats?.points ?? 0}</b>
            <span>ruutua</span>
            <span>pistettä</span>
            {app.weekly?.goal > 0 && (
              <span class="week-chip">Viikko {app.weekly.this_week}/{app.weekly.goal}</span>
            )}
          </div>
          <PlayerBadge app={app} />
          {app.mapModes.length > 0 && (
            <button class="filter-chip" aria-label="Poista kulkutapasuodatus" onClick={() => app.setMapModes([])}>
              {modesText(app.mapModes, modeLabel)} <span aria-hidden="true">✕</span>
            </button>
          )}
        </div>
        <div class="map-tools">
          <button
            class="round"
            aria-label="Keskitä omaan sijaintiin"
            aria-pressed={follow}
            onClick={() => {
              setFollow(true);
              if (me) mapRef.current?.setView([me.latitude, me.longitude], Math.max(mapRef.current.getZoom(), 15));
              else app.locateOnce();
            }}
          >
            <IconLocate />
          </button>
          <button class="round" aria-label="Kartan kerrokset" aria-pressed={layersOpen} aria-expanded={layersOpen} onClick={() => setLayersOpen(!layersOpen)}>
            <IconLayers />
          </button>
          <button class="round" aria-label="Karttamerkkien selite" aria-pressed={legend} onClick={() => setLegend(!legend)}>
            <IconLegend />
          </button>
          <button class="round" aria-label="Seikkailukortit" aria-haspopup="dialog" aria-pressed={Boolean(app.adventure)} onClick={() => setAdventureOpen(true)}>
            <IconAdventure />
          </button>
          <button
            class="round"
            aria-label="Lisää ruutu käsin"
            aria-pressed={tool?.type === "tile"}
            onClick={() => (tool?.type === "tile" ? app.endTool() : app.startTool({ type: "tile", key: null }))}
          >
            <IconAddTile />
          </button>
        </div>
      </div>

      {simulator && <div class="dev-strip">Simulaattori</div>}

      {layersOpen && (
        <div class="layer-panel" role="group" aria-label="Kartan kerrokset ja suodatus">
          <h3 class="panel-h">Karttapohja</h3>
          <div role="radiogroup" aria-label="Karttapohja" class="layer-group">
            {BASEMAPS.map((b) => {
              const server = app.mapLayers?.find((l) => l.id === b.id);
              const unavailable = b.proxied && !server?.available;
              return (
                <button
                  key={b.id}
                  role="radio"
                  aria-checked={app.basemap === b.id}
                  disabled={unavailable}
                  class="layer-opt"
                  onClick={() => app.setBasemap(b.id)}
                >
                  <b>{b.name}</b>
                  <span>{unavailable ? "Ei käytössä: palvelimelta puuttuu MML:n API-avain" : b.hint}</span>
                </button>
              );
            })}
          </div>

          <h3 class="panel-h">Offline-kartta</h3>
          {app.basemap && app.basemap !== "osm" ? (
            <OfflineMapPanel layer={app.basemap} mapRef={mapRef} />
          ) : (
            <p class="s panel-note">Valitse Maastokartta tai Taustakartta, niin voit ladata alueen offline-käyttöön.</p>
          )}

          <h3 class="panel-h">Näytä kartalla</h3>
          {OVERLAY_OPTIONS.filter((o) => !o.needsFamily || (app.players?.length ?? 0) > 1).map(({ id, label }) => (
            <label class="check small" key={id}>
              <input type="checkbox" checked={Boolean(app.overlays[id])} onChange={(e) => app.setOverlay(id, e.currentTarget.checked)} />
              <span>{label}</span>
            </label>
          ))}

          <h3 class="panel-h" id="mode-filter-h">Kulkutapa</h3>
          <div class="chips" role="group" aria-labelledby="mode-filter-h">
            <button class="chip" role="checkbox" aria-checked={app.mapModes.length === 0} onClick={() => app.setMapModes([])}>Kaikki</button>
            {FILTER_MODES.map((m) => (
              <button
                key={m.id}
                class="chip"
                role="checkbox"
                aria-checked={app.mapModes.includes(m.id)}
                onClick={() => app.setMapModes(app.mapModes.includes(m.id) ? app.mapModes.filter((x) => x !== m.id) : [...app.mapModes, m.id])}
              >
                {SHORT_MODE[m.id] ?? m.label}
              </button>
            ))}
          </div>
          {app.mapModes.length > 0 && <p class="s panel-note">Näkyvissä ruudut, joissa olet käynyt valitulla kulkutavalla. Käsin lisätyillä ruuduilla ei ole kulkutapaa, joten ne piilotetaan.</p>}

          <h3 class="panel-h">Työkalut</h3>
          <button
            class="layer-opt"
            onClick={() => {
              setLayersOpen(false);
              app.startTool({ type: "time", tiles: null });
            }}
          >
            <b>Aikakone</b>
            <span>Tutkimuskartta valittuna päivänä</span>
          </button>
          {app.admin && (
            <button
              class="layer-opt"
              onClick={() => {
                setLayersOpen(false);
                app.startTool({ type: "impossible", level: "tile", key: null });
              }}
            >
              <b>Merkitse mahdoton ruutu</b>
              <span>Rajavyöhyke, järvi tai muu, jonne ei pääse</span>
            </button>
          )}
        </div>
      )}

      {legend && (
        <div class="legend" role="note" aria-label="Selite">
          <div><i class="sw sw-open" /> Oma kerätty ruutu</div>
          <div><i class="sw sw-pending" /> Odottaa tallennusta</div>
          <div><i class="sw sw-big" /> Suuri ruutu, vierailtu</div>
          <div><i class="sw sw-rasti" /> Valmis kohde</div>
          <div><i class="sw sw-route" /> Valmis reitti</div>
          {app.adventure && <div><i class="sw sw-adv" /> Seikkailun reitti ja rastit</div>}
          {shapeLegend(app.overlays).map((r) => <div key={r.cls}><i class={`sw ${r.cls}`} /> {r.text}</div>)}
          {app.overlays.impossible && Boolean(tiles?.impossible?.length) && <div><i class="sw sw-blocked" /> Mahdoton ruutu</div>}
          {family && family.legend.map((l) => (
            <div key={l.id}><i class="sw" style={{ background: `color-mix(in srgb, ${l.color} 40%, transparent)`, border: `1.5px solid ${l.color}` }} /> {l.name}: {l.count} ruutua</div>
          ))}
          {family && family.shared > 0 && <div><i class="sw sw-shared" /> Yhteinen ruutu ({family.shared})</div>}
          {app.overlays.heat && (
            <div class="heat-legend" aria-label="Lämpökartan asteikko, retkiä solussa">
              {HEAT_STEPS.map((h) => <i key={h.min} style={{ background: h.color }} title={h.label} />)}
              <span>1 → 10+ retkeä</span>
            </div>
          )}
        </div>
      )}

      {(tool?.type === "poi" || tool?.type === "hiddenpick") && <div class="crosshair" aria-hidden="true"><i /></div>}

      <div class="dock">
        {!tool && <RouteBar app={app} />}
        {!tool && <PoiBar app={app} />}
        {!tool && <AdventureBar app={app} onStart={() => setPicker("start")} />}
        {tool?.type === "poi" ? (
          <PoiPlacePanel app={app} tool={tool} mapRef={mapRef} onRadius={(radius) => app.setTool((t) => ({ ...t, radius }))} />
        ) : tool?.type === "hiddenpick" ? (
          <HiddenPickPanel app={app} tool={tool} mapRef={mapRef} />
        ) : tool?.type === "route" ? (
          <RouteDrawPanel
            app={app}
            tool={tool}
            onUndo={() => app.setTool((t) => ({ ...t, coords: t.coords.slice(0, -1) }))}
            onClear={() => app.setTool((t) => ({ ...t, coords: [] }))}
          />
        ) : tool?.type === "tile" ? (
          <TilePickPanel app={app} tool={tool} />
        ) : tool?.type === "impossible" ? (
          <ImpossiblePanel app={app} tool={tool} />
        ) : tool?.type === "time" ? (
          <TimePanel app={app} tool={tool} />
        ) : !active ? (
          <button class="start-btn" onClick={() => setPicker("start")}>
            <IconStart /> Aloita retki
          </button>
        ) : (
          <section class="recording" aria-label="Retki käynnissä">
            <div class="rec-head">
              <div class="rec-state" role="status">
                {active.pausedAt ? <><i class="pause-mark" aria-hidden="true" /> Tauolla</> : <><i class="pulse" aria-hidden="true" /> Tallentaa</>}
              </div>
              <button class="btn btn-quiet mode-switch" aria-label={`Kulkutapa ${modeLabel(active.mode)}, vaihda`} onClick={() => setPicker("change")}>
                {modeLabel(active.mode)} <IconChevron />
              </button>
            </div>
            <div class="rec-figures">
              <div><b class="num">{duration(elapsed)}</b><span>{active.pausedTotal || active.pausedAt ? "liikkeellä" : "kesto"}</span></div>
              <div><b class="num">{km(active.distanceM).replace(" km", "")}</b><span>km</span></div>
              <div>
                <b class="num">{active.newTiles + pendingTiles}</b>
                <span>{pendingTiles ? `uutta, ${pendingTiles} odottaa` : "uutta ruutua"}</span>
              </div>
            </div>
            {active.pausedAt && <div class="transfer-banner">Tauon aikana sijaintia ei tallenneta. Voit siirtyä vapaasti, matka ei kerry.</div>}
            {!active.pausedAt && !isEligible(active.mode) && (
              <div class="transfer-banner">Siirtymä käynnissä: ruutuja ei kerätä ennen kuin vaihdat lihasvoimaiseen kulkutapaan.</div>
            )}
            <div class="rec-actions">
              {active.pausedAt ? (
                <button class="btn btn-rasti" onClick={app.resumeTrip}>Jatka retkeä</button>
              ) : (
                <button class="btn" onClick={app.pauseTrip}>Tauko</button>
              )}
              <button class="btn btn-primary" onClick={() => setConfirmStop(true)}>Lopeta retki</button>
            </div>
            <RecStatusLines rec={rec} me={app.me} now={now} paused={Boolean(active.pausedAt)} />
            {app.locationError && <p class="rec-note warn" role="alert">{app.locationError}</p>}
          </section>
        )}
      </div>

      {adventureOpen && (
        <AdventureSheet
          app={app}
          onClose={() => setAdventureOpen(false)}
          onPick={(card, mates) => {
            setAdventureOpen(false);
            app.chooseAdventure(card, mates);
          }}
        />
      )}
      {app.adventureDone && <AdventureDoneSheet done={app.adventureDone} onClose={app.closeAdventureDone} />}
      {picker === "start" && (
        <ModePicker
          title="Miten liikut?"
          hint="Valitse kulkutapa. Voit vaihtaa sitä retken aikana ja korjata jälkikäteen."
          extra={<CompanionPicker app={app} value={app.companions} onChange={app.setCompanions} />}
          current="walk"
          onClose={() => setPicker(null)}
          onPick={(mode) => {
            setPicker(null);
            app.startTrip(mode);
          }}
        />
      )}
      {picker === "change" && (
        <ModePicker
          title="Vaihda kulkutapa"
          hint="Uusi kulkutapa alkaa nyt. Aiempi osuus säilyy entisellään."
          current={active?.mode}
          onClose={() => setPicker(null)}
          onPick={(mode) => {
            setPicker(null);
            app.changeMode(mode);
          }}
        />
      )}
      {confirmStop && (
        <Sheet title="Lopetetaanko retki?" onClose={() => setConfirmStop(false)}>
          <p class="hint">Kulkutavat ja puuttuvat suoritukset voi korjata retken tiedoista päiväkirjassa.</p>
          <div class="sheet-actions">
            <button
              class="btn btn-primary btn-block"
              onClick={() => {
                setConfirmStop(false);
                app.finishTrip();
              }}
            >
              Lopeta ja tallenna
            </button>
            <button class="btn btn-block" onClick={() => setConfirmStop(false)}>Jatka retkeä</button>
            {/* Vahingossa aloitettu retki: ei tallenneta lainkaan. Erillinen varmistus estää vahinkopoiston. */}
            <button
              class="btn btn-quiet btn-danger btn-block"
              onClick={() => {
                setConfirmStop(false);
                setConfirmDiscard(true);
              }}
            >
              Hylkää retki, älä tallenna
            </button>
          </div>
        </Sheet>
      )}
      {confirmDiscard && active && (() => {
        const summary = discardSummary({ distanceM: active.distanceM, tiles: active.newTiles + pendingTiles, points: active.earned });
        return (
          <Sheet title="Hylätäänkö retki?" onClose={() => setConfirmDiscard(false)}>
            <p class="hint" role="alert">{summary.text}</p>
            <div class="sheet-actions">
              <button
                class="btn btn-danger btn-block"
                onClick={() => {
                  setConfirmDiscard(false);
                  app.discardTrip();
                }}
              >
                Hylkää retki
              </button>
              <button class="btn btn-primary btn-block" onClick={() => setConfirmDiscard(false)}>Jatka retkeä</button>
            </div>
          </Sheet>
        );
      })()}
    </div>
  );
}




/** GPS:n ja tallennuksen tila retken aikana: elossa oleva GPS, sen tarkkuus ja se, onko tieto turvassa. */
function RecStatusLines({ rec, me, now, paused }) {
  const save = saveStatus(rec);
  // Tauon aikana GPS:ää ei seurata, joten siitä ei huomauteta.
  const gps = paused ? null : gpsStatus(me, now);
  return (
    <div class="rec-status" role="status">
      {gps && <p class={`rec-line ${gps.level}`}><i aria-hidden="true" /> {gps.text}</p>}
      <p class={`rec-line ${save.level}`}><i aria-hidden="true" /> {save.text}</p>
    </div>
  );
}
